import { useState } from "react";
import { Btn } from "../components/ui/Button";
import { Logo } from "../components/Logo";
import { useResponsive } from "../hooks/useResponsive";

const NAV_LINKS = ["Home", "Features", "Pricing", "About", "Contact"];

export function Landing({ onNav, isAuthenticated = false, user = null }) {
  const { isMobile, isTablet } = useResponsive();
  const features = [
    {
      icon: "🌐",
      title: "Decentralized Storage",
      text: "Files distributed across a global network for ultimate reliability",
    },
    {
      icon: "♾️",
      title: "Unlimited Scale",
      text: "Store as much as you need with no hard limits or surprise fees",
    },
    {
      icon: "🔐",
      title: "Bank-Level Security",
      text: "End-to-end encryption with secure key management built in",
    },
    {
      icon: "⚡",
      title: "Fast & Reliable",
      text: "Lightning-fast transfers with built-in redundancy and failover",
    },
  ];

  const pricing = [
    {
      tier: "Free",
      price: "$0",
      storage: "5 GB",
      features: [
        "Basic upload & download",
        "File sharing",
        "Mobile access",
        "Email support",
      ],
      accent: false,
    },
    {
      tier: "Pro",
      price: "$4.99",
      storage: "50 GB",
      features: [
        "Everything in Free",
        "Priority upload queue",
        "Advanced file manager",
        "Priority support",
        "Version history",
      ],
      accent: true,
    },
    {
      tier: "Enterprise",
      price: "Custom",
      storage: "Unlimited",
      features: [
        "Everything in Pro",
        "API access",
        "Custom domain",
        "Dedicated storage nodes",
        "SLA guarantee",
        "24/7 support",
      ],
      accent: false,
    },
  ];

  const faqs = [
    {
      q: "What is decentralized storage?",
      a: "Your files are stored across a distributed network, meaning no single point of failure. Even if one node goes down, your files remain accessible.",
    },
    {
      q: "How secure is my data?",
      a: "All files are encrypted end-to-end before leaving your device. Only you hold the encryption keys — not even we can access your files.",
    },
    {
      q: "Can I upgrade or downgrade anytime?",
      a: "Yes. Plan changes take effect immediately. Upgrades unlock capacity right away; downgrades apply at your next billing cycle.",
    },
    {
      q: "What file types are supported?",
      a: "Any file type is supported — documents, images, videos, archives, code, and more. No restrictions on content type.",
    },
    {
      q: "Is there a file size limit?",
      a: "Free accounts support files up to 5 GB per upload. Pro and Enterprise have no per-file size limit.",
    },
    {
      q: "Do you keep backups of my files?",
      a: "Yes. Files are redundantly stored across multiple nodes, so your data is always safe even if individual storage nodes experience issues.",
    },
  ];

  const [openFaq, setOpenFaq] = useState(null);
  const dashboardTarget = user?.role === "admin" ? "admin" : "dashboard";
  const dashboardLabel = user?.role === "admin" ? "Open Admin" : "Open Dashboard";
  const sectionPadding = isMobile ? "44px 18px" : isTablet ? "56px 24px" : "60px 40px";
  const heroPadding = isMobile ? "72px 18px 48px" : isTablet ? "96px 24px 64px" : "120px 40px 80px";
  const heroTitleSize = isMobile ? 42 : isTablet ? 54 : 68;
  const heroBodySize = isMobile ? 16 : isTablet ? 18 : 20;
  const featuresColumns = isMobile ? "1fr" : isTablet ? "repeat(2, minmax(0,1fr))" : "repeat(4, minmax(0,1fr))";
  const pricingColumns = isMobile ? "1fr" : isTablet ? "repeat(2, minmax(0,1fr))" : "repeat(3, minmax(0,1fr))";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0f172a",
        color: "#e2e8f0",
        fontFamily: "'Space Grotesk', 'Space Mono', system-ui",
      }}
    >
      {/* Header */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "rgba(15,23,42,0.85)",
          backdropFilter: "blur(16px)",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          padding: isMobile ? "0 16px" : isTablet ? "0 24px" : "0 40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 64,
          gap: 12,
        }}
      >
        <Logo onClick={() => onNav("landing")} />
        <nav style={{ display: isMobile ? "none" : "flex", gap: isTablet ? 18 : 32, flexWrap: "wrap", justifyContent: "center" }}>
          {NAV_LINKS.map((l) => (
            <button
              key={l}
              type="button"
              style={{
                color: "#94a3b8",
                fontSize: 14,
                fontWeight: 500,
                textDecoration: "none",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              {l}
            </button>
          ))}
        </nav>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {isAuthenticated ? (
            <Btn size="sm" onClick={() => onNav(dashboardTarget)}>
              {dashboardLabel}
            </Btn>
          ) : (
            <>
              <Btn variant="ghost" size="sm" onClick={() => onNav("signin")}>
                Sign In
              </Btn>
              <Btn size="sm" onClick={() => onNav("signup")}>
                Sign Up Free
              </Btn>
            </>
          )}
        </div>
      </header>

      {/* Hero */}
      <section
        style={{
          padding: heroPadding,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(59,130,246,0.18) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            background: "rgba(59,130,246,0.12)",
            border: "1px solid rgba(59,130,246,0.3)",
            borderRadius: 99,
            padding: "5px 14px",
            marginBottom: 28,
            fontSize: 12,
            fontWeight: 600,
            color: "#93c5fd",
            letterSpacing: "0.08em",
          }}
        >
          ✦ NOW IN OPEN BETA — FREE FOR ALL USERS
        </div>
        <h1
          style={{
            fontSize: heroTitleSize,
            fontWeight: 900,
            lineHeight: 1.05,
            letterSpacing: "-0.05em",
            marginBottom: 20,
            background: "linear-gradient(135deg,#ffffff 0%,#93c5fd 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Unlimited Cloud
          <br />
          Storage. Zero Boundaries.
        </h1>
        <p
          style={{
            fontSize: heroBodySize,
            color: "#94a3b8",
            maxWidth: 520,
            margin: "0 auto 40px",
            lineHeight: 1.6,
          }}
        >
          Store, share, and access your files anywhere with military-grade
          security and decentralized redundancy.
        </p>
        <div style={{ display: "flex", gap: 14, justifyContent: "center", flexDirection: isMobile ? "column" : "row", alignItems: "center" }}>
          <Btn size="lg" onClick={() => onNav(isAuthenticated ? dashboardTarget : "signup")}>
            {isAuthenticated ? `${dashboardLabel} →` : "Get Started Free →"}
          </Btn>
          <Btn variant="secondary" size="lg">
            Watch Demo
          </Btn>
        </div>

        {/* Hero visual */}
        <div
          style={{
            marginTop: 72,
            display: "flex",
            justifyContent: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          {["2.4 TB stored", "98.9% uptime", "340K+ files", "12 global nodes"].map(
            (s) => (
              <div
                key={s}
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 10,
                  padding: "10px 20px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#cbd5e1",
                }}
              >
                {s}
              </div>
            )
          )}
        </div>
      </section>

      {/* Features */}
      <section style={{ padding: sectionPadding, maxWidth: 1100, margin: "0 auto" }}>
        <h2
          style={{
            fontSize: 36,
            fontWeight: 800,
            textAlign: "center",
            marginBottom: 8,
            letterSpacing: "-0.04em",
          }}
        >
          Everything you need in one vault
        </h2>
        <p
          style={{
            color: "#64748b",
            textAlign: "center",
            marginBottom: 48,
            fontSize: 16,
          }}
        >
          Built for individuals and teams who demand reliability and security.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: featuresColumns,
            gap: 20,
          }}
        >
          {features.map((f) => (
            <div
              key={f.title}
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 16,
                padding: "28px 24px",
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 14 }}>{f.icon}</div>
              <h3
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  marginBottom: 8,
                  color: "#f1f5f9",
                }}
              >
                {f.title}
              </h3>
              <p
                style={{
                  fontSize: 14,
                  color: "#64748b",
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                {f.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section style={{ padding: sectionPadding, maxWidth: 1000, margin: "0 auto" }}>
        <h2
          style={{
            fontSize: 36,
            fontWeight: 800,
            textAlign: "center",
            marginBottom: 8,
            letterSpacing: "-0.04em",
          }}
        >
          Simple, transparent pricing
        </h2>
        <p
          style={{
            color: "#64748b",
            textAlign: "center",
            marginBottom: 48,
            fontSize: 16,
          }}
        >
          Start free. Scale as you grow. No surprises.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: pricingColumns,
            gap: 20,
          }}
        >
          {pricing.map((p) => (
            <div
              key={p.tier}
              style={{
                background: p.accent
                  ? "rgba(59,130,246,0.1)"
                  : "rgba(255,255,255,0.03)",
                border: p.accent
                  ? "2px solid #3b82f6"
                  : "1px solid rgba(255,255,255,0.08)",
                borderRadius: 18,
                padding: 28,
                position: "relative",
              }}
            >
              {p.accent && (
                <div
                  style={{
                    position: "absolute",
                    top: -12,
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "#3b82f6",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "3px 12px",
                    borderRadius: 99,
                    letterSpacing: "0.06em",
                  }}
                >
                  MOST POPULAR
                </div>
              )}
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#94a3b8",
                  letterSpacing: "0.08em",
                  marginBottom: 8,
                }}
              >
                {p.tier.toUpperCase()}
              </div>
              <div
                style={{
                  fontSize: 40,
                  fontWeight: 900,
                  color: "#fff",
                  letterSpacing: "-0.04em",
                  marginBottom: 4,
                }}
              >
                {p.price}
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: "#94a3b8",
                  }}
                >
                  {p.price.includes("$") ? "/mo" : ""}
                </span>
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: "#3b82f6",
                  fontWeight: 700,
                  marginBottom: 20,
                }}
              >
                {p.storage} storage
              </div>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: "0 0 24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                {p.features.map((f) => (
                  <li
                    key={f}
                    style={{
                      fontSize: 13,
                      color: "#94a3b8",
                      display: "flex",
                      gap: 8,
                    }}
                  >
                    <span style={{ color: "#22c55e" }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>
              <Btn
                variant={p.accent ? "primary" : "secondary"}
                style={{ width: "100%", justifyContent: "center" }}
                onClick={() => onNav("signup")}
              >
                {p.tier === "Enterprise" ? "Contact Sales" : "Choose Plan"}
              </Btn>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section
        style={{
          padding: sectionPadding,
          maxWidth: 720,
          margin: "0 auto",
        }}
      >
        <h2
          style={{
            fontSize: 36,
            fontWeight: 800,
            textAlign: "center",
            marginBottom: 40,
            letterSpacing: "-0.04em",
          }}
        >
          Frequently asked questions
        </h2>
        {faqs.map((f, i) => (
          <div
            key={i}
            style={{
              borderBottom: "1px solid rgba(255,255,255,0.07)",
              padding: "18px 0",
            }}
          >
            <button
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
              style={{
                width: "100%",
                textAlign: "left",
                background: "none",
                border: "none",
                color: "#e2e8f0",
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: 0,
              }}
            >
              {f.q}
              <span
                style={{
                  color: "#3b82f6",
                  fontSize: 18,
                  transition: "transform 0.2s",
                  display: "inline-block",
                  transform: openFaq === i ? "rotate(45deg)" : "rotate(0deg)",
                }}
              >
                +
              </span>
            </button>
            {openFaq === i && (
              <p
                style={{
                  color: "#64748b",
                  fontSize: 14,
                  lineHeight: 1.7,
                  marginTop: 12,
                  marginBottom: 0,
                }}
              >
                {f.a}
              </p>
            )}
          </div>
        ))}
      </section>

      {/* Footer */}
      <footer
        style={{
          borderTop: "1px solid rgba(255,255,255,0.07)",
          padding: isMobile ? "28px 18px" : "40px",
          textAlign: "center",
          color: "#334155",
          fontSize: 13,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 28,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          {["About", "Privacy Policy", "Terms of Service", "Contact"].map((l) => (
            <button
              key={l}
              type="button"
              style={{
                color: "#64748b",
                textDecoration: "none",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              {l}
            </button>
          ))}
        </div>
        © 2026 HexVault · hexvault.in · All rights reserved
      </footer>
    </div>
  );
}
